import React, { useState } from 'react';
import Label from '../components/atoms/Label';
import Button from '../components/atoms/Button';
import TextFieldWithImage from '../components/atoms/TextFieldWithImage';
import userIcon from '../assets/user_icon.svg';
import ic_forgot_email from '../assets/ic_forgot_email.svg';
import ic_password_reset from '../assets/ic_password_reset.svg';
import ic_reset_password_forgot from '../assets/ic_reset_password_forgot.svg';
import ic_reset_done from '../assets/ic_reset_done.svg';
import { useRef } from 'react';
import { useForgotLogin } from '../hooks/forgotLogin';

const STEPS = {
	EMAIL: 'email',
	OTP: 'otp',
	NEW_PASSWORD: 'new_password',
	SUCCESS: 'success',
};

function ForgotPassword({ onBackToLogin, initialEmail = '' }) {
	const [currentStep, setCurrentStep] = useState(STEPS.EMAIL);
	const [email, setEmail] = useState(initialEmail);
	const [otp, setOtp] = useState(['', '', '', '', '', '']);
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	//   const [error, setError] = useState("");
	//   const [loading, setLoading] = useState(false);
	const inputs = useRef([]);
	const [isCodeComplete, setIsCodeComplete] = useState(false);

	const { sendOTPToEmail, loading, error, setError, verifyOTP, resetPassword } = useForgotLogin();

	const handleEmailSubmit = async () => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Simple regex to validate email format
		if (!email.trim()) {
			setError('Email is required.');
			return;
		} else if (!emailRegex.test(email)) {
			setError('Enter a valid email address.');
			return;
		}
		// API call to send OTP
		if (await sendOTPToEmail(email)) {
			setCurrentStep(STEPS.OTP);
		}
	};

	const handleOTPVerify = async () => {
		if (otp.some((digit) => digit === '')) {
			setError('Please enter all 6 digits of the OTP');
			return;
		}
		try {
			// API call to verify OTP
			if (await verifyOTP(email, otp.join(''))) {
				setCurrentStep(STEPS.NEW_PASSWORD);
			}
		} catch (err) {
			setError('Invalid OTP. Please try again.');
		}
	};

	const handlePasswordReset = async () => {
		if (newPassword !== confirmPassword) {
			setError('Passwords do not match');
			return;
		}
		if (newPassword.length < 8) {
			setError('Password must be at least 8 characters');
			return;
		}
		try {
			// API call to reset password
			if (await resetPassword(email, newPassword)) {
				setError('');
				setCurrentStep(STEPS.SUCCESS);
			}
		} catch (err) {
			setError('Failed to reset password. Please try again.');
		}
	};

	const handleOTPChange = (value, index) => {
		if (value.match(/^[0-9]$/)) {
			const newOTP = [...otp];
			newOTP[index] = value;
			setOtp(newOTP);
			if (index < 5 && inputs.current[index + 1]) {
				inputs.current[index + 1].focus();
			}
			setIsCodeComplete(newOTP.every((digit) => digit !== ''));
		}
	};

	const handleBackspace = (index) => {
		const newOTP = [...otp];
		newOTP[index] = '';
		setOtp(newOTP);
		setIsCodeComplete(newOTP.every((digit) => digit !== ''));

		if (index > 0) {
			inputs.current[index - 1].focus();
		}
	};

	const renderEmailStep = () => (
		<>
			<img src={ic_forgot_email} alt="user" />
			<Label
				label="Forgot Password ?"
				fontSize="25px"
				padding="10px"
				color="#000000"
				fontWeight="700"
			/>
			<Label
				label="No worries, we will send you reset instructions."
				fontSize="14px"
				padding="10px"
				fontWeight="400"
				color="#000000"
			/>
			<TextFieldWithImage
				placeholder="Enter your Email"
				src={userIcon}
				value={email}
				onChange={(e) => {
					setEmail(e);
					setError('');
				}}
				width="100%"
			/>

			<Button
				className="login-button"
				label={loading ? 'Reseting...' : 'Reset Password'}
				style={{ width: '100%', marginTop: '20px' }}
				onClick={handleEmailSubmit}
				isLoading={loading}
				disabled={loading}
			></Button>
		</>
	);

	const renderOTPStep = () => (
		<>
			<img src={ic_password_reset} alt="user" />
			<Label
				label="Password reset"
				fontSize="24px"
				padding="10px"
				color="#000000"
				fontWeight="700"
			/>
			<Label
				label={`We sent a code to ${email}`}
				fontSize="14px"
				padding="10px"
				color="#000000"
				fontWeight="400"
			/>
			<div
				style={{ display: 'flex', gap: '5px' }}
				onKeyDown={(e) => {
					if (e.key === 'Enter') {
						handleOTPVerify();
					}
				}}
				tabIndex={0}
			>
				{otp?.map((digit, index) => (
					<input
						key={index}
						type="text"
						value={digit}
						onChange={(e) => {
							handleOTPChange(e.target.value, index);
							setError('');
						}}
						onKeyDown={(e) => e.key === 'Backspace' && handleBackspace(index)}
						maxLength={1}
						style={{
							width: '40px',
							height: '40px',
							fontSize: '20px',
							textAlign: 'center',
							backgroundColor: '#D9D9D9',
						}}
						ref={(el) => (inputs.current[index] = el)}
					/>
				))}
			</div>
			<Button
				className="login-button"
				label={loading ? 'Continue...' : 'Continue'}
				style={{ width: '100%', marginTop: '20px' }}
				onClick={() => {
					handleOTPVerify();
					setError('');
				}}
				isLoading={loading}
				disabled={loading || !isCodeComplete}
			></Button>

			<div style={{ marginTop: '10px' }}>
				<span>Didn't receive the email? </span>
				<Label
					label="Click to resend"
					fontSize="14px"
					color="#08BD80"
					fontWeight="700"
					style={{ cursor: 'pointer' }}
					onClick={handleEmailSubmit}
				/>
			</div>
		</>
	);

	const renderNewPasswordStep = () => (
		<>
			<img src={ic_reset_password_forgot} alt="user" />
			<Label
				label="Set new password"
				fontSize="24px"
				padding="10px"
				color="#000000"
				fontWeight="700"
			/>
			<Label
				label="Must be atleast 8 characters."
				fontSize="14px"
				padding="10px"
				color="#000000"
				fontWeight="400"
			/>
			<TextFieldWithImage
				placeholder="Password"
				type="password"
				value={newPassword}
				onChange={setNewPassword}
				width="100%"
			/>
			<TextFieldWithImage
				placeholder="Confirm password"
				type="password"
				value={confirmPassword}
				onChange={setConfirmPassword}
				width="100%"
			/>
			<Button
				className="login-button"
				label={loading ? 'Resetting...' : 'Reset Password'}
				style={{ width: '100%', marginTop: '20px' }}
				onClick={handlePasswordReset}
				isLoading={loading}
				disabled={loading || !isCodeComplete}
			></Button>
		</>
	);

	const renderSuccessStep = () => (
		<>
			<img src={ic_reset_done} alt="user" />
			<Label
				label="All done !"
				fontSize="24px"
				padding="10px"
				color="#000000"
				fontWeight="700"
			/>
			<Label
				label="Your Password has been reset"
				fontSize="14px"
				padding="10px"
				color="#000000"
				fontWeight="400"
			/>
			<Button
				className="login-button"
				label="Back to Login Page"
				onClick={onBackToLogin}
				style={{ width: '100%', marginTop: '20px' }}
			/>
		</>
	);

	const renderCurrentStep = () => {
		switch (currentStep) {
			case STEPS.EMAIL:
				return renderEmailStep();
			case STEPS.OTP:
				return renderOTPStep();
			case STEPS.NEW_PASSWORD:
				return renderNewPasswordStep();
			case STEPS.SUCCESS:
				return renderSuccessStep();
			default:
				return null;
		}
	};

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				padding: '40px',
				maxWidth: '400px',
				margin: '0 auto',
			}}
		>
			{renderCurrentStep()}
			{currentStep !== STEPS.SUCCESS && (
				<div
					style={{
						display: 'flex',
						width: '100%',
						justifyContent: 'center',
						marginTop: '20px',
						cursor: 'pointer',
					}}
					onClick={onBackToLogin}
				>
					← Back to log in
				</div>
			)}
			{error && <div style={{ color: 'red', marginTop: '10px' }}>{error}</div>}
		</div>
	);
}

export default ForgotPassword;
